const LOCALES: Record<string, string> = { en: 'en', de: 'de', es: 'es', pt: 'pt-BR' };

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "2 hours ago" / "hace 2 horas" / "vor 2 Stunden" / "há 2 horas" for the
 * notification list (chamba-ai#411), in the app's current language, via
 * Intl - no date library. Anything a week old or older falls back to a short
 * date. Returns '' for a missing or unparseable timestamp.
 *
 * Timestamps from the API are explicit UTC (they end in `Z`), so `Date.parse`
 * reads them as the instant they are and not as browser-local time.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  lang: string,
  now: Date = new Date()
): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';

  const locale = LOCALES[lang] ?? 'en';
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  // Negative = in the past. A timestamp slightly in the future (clock skew
  // between server and browser) is shown as "now", never as "in 3 minutes".
  const seconds = Math.min(0, Math.round((then - now.getTime()) / 1000));
  const age = Math.abs(seconds);

  if (age < 45) return rtf.format(0, 'second');
  if (age < HOUR) return rtf.format(-Math.max(1, Math.round(age / MINUTE)), 'minute');
  if (age < DAY) return rtf.format(-Math.round(age / HOUR), 'hour');
  if (age < 7 * DAY) return rtf.format(-Math.round(age / DAY), 'day');

  const date = new Date(then);
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
}
